/*
 *
 * user.js
 *
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import userAPI from "../../api/userAPI";
import { fetchContractorTasks } from "./task";
import { login } from "./session";
import thunk from "../../helpers/thunk";

/* thunks */
const fetchUsers = thunk("user/fetchUsers", userAPI.fetchAll, (o) => o.r.data);

const createUser = thunk("user/createUser", userAPI.create, (o) => o.r.data);

const deleteUser = thunk("user/deleteUser", userAPI.delete, (o) => o.d);

const setUserPassword = thunk(
  "user/setUserPassword",
  userAPI.setPassword,
  (o) => o.r.data
);

const setUserActive = thunk("user/setUserActive", userAPI.setActive, (o) => ({
  userIDs: o.d.userIDs,
  active: o.d.active,
}));

const saveUserModifications = createAsyncThunk(
  "user/saveUserModifications",
  async (data, { rejectWithValue, getState }) => {
    const ID = [];
    const userName = [];
    const firstName = [];
    const lastName = [];
    const email = [];
    const role = [];
    const active = [];

    Object.keys(getState().user.users).forEach((userID) => {
      const user = getState().user.users[userID];

      if (user.modified) {
        ID.push(userID);
        userName.push(user.username);
        firstName.push(user.firstname);
        lastName.push(user.lastname);
        email.push(user.email);
        role.push(user.role);
        active.push(user.active);
      }
    });
    return await userAPI
      .saveModifications({
        ID,
        userName,
        firstName,
        lastName,
        email,
        role,
        active,
      })
      .then((r) => {
        return {
          IDs: ID,
        };
      })
      .catch((e) => rejectWithValue());
  }
);

/* slice */
const slice = createSlice({
  name: "user",
  initialState: {
    users: {},
  },
  reducers: {
    userNameModified: (state, action) => {
      const user = state.users[action.payload.userID];
      user.modified = true;
      user.username = action.payload.userName;
    },
    firstNameModified: (state, action) => {
      const user = state.users[action.payload.userID];
      user.modified = true;
      user.firstname = action.payload.firstName;
    },
    lastNameModified: (state, action) => {
      const user = state.users[action.payload.userID];
      user.modified = true;
      user.lastname = action.payload.lastName;
    },
    emailModified: (state, action) => {
      const user = state.users[action.payload.userID];
      user.modified = true;
      user.email = action.payload.email;
    },
    roleModified: (state, action) => {
      const user = state.users[action.payload.userID];
      user.modified = true;
      user.role = action.payload.role;
    },
    activeModified: (state, action) => {
      const user = state.users[action.payload.userID];
      user.modified = true;
      user.active = action.payload.active;
    },
  },
  extraReducers: {
    [fetchUsers.fulfilled]: (state, action) => {
      const users = action.payload;

      users.forEach((user) => {
        state.users[user.ID] = {
          ...user,
        };
        delete state.users[user.ID].ID;
      });
    },
    [createUser.fulfilled]: (state, action) => {
      const user = action.payload;

      state.users[user.ID] = {
        ...user,
      };
      delete state.users[user.ID].ID;
    },
    [fetchContractorTasks.fulfilled]: (state, action) => {
      const tTasks = action.payload.data.t;
      const eTasks = action.payload.data.e;
      const userID = action.payload.userID;

      state.users[userID].assignedTasks = {
        t: tTasks.map((task) => task.id),
        e: eTasks.map((task) => task.id),
      };
    },
    [login.fulfilled]: (state, action) => {
      state.users[action.payload.userID] = {
        userName: action.payload.userName,
        firstName: action.payload.firstName,
        role: action.payload.role,
      };
    },
    [setUserActive.fulfilled]: (state, action) => {
      const userIDs = action.payload.userIDs;
      const active = action.payload.active;

      userIDs.forEach((ID) => {
        state.users[ID].active = active;
      });
    },
    [saveUserModifications.fulfilled]: (state, action) => {
      const IDs = action.payload.IDs;

      IDs.forEach((userID, index) => {
        delete state.users[userID].modified;
      });
    },
    [deleteUser.fulfilled]: (state, action) => {
      const userID = action.payload.userID;
      delete state.users[userID];
    },
  },
});

export {
  fetchUsers,
  createUser,
  setUserActive,
  setUserPassword,
  saveUserModifications,
  deleteUser,
};
export const {
  userNameModified,
  firstNameModified,
  lastNameModified,
  emailModified,
  roleModified,
  activeModified,
} = slice.actions;
export default slice.reducer;
